/**
 * Upload page — navigates to /column-preview after sheet selection.
 *
 * Two input sources behind one segmented control (gsheet-io-01 / S5.1): a file, or a
 * Google Sheet link. Both end up producing the same UploadResponse, so everything from
 * column confirmation onwards is a single code path.
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import GoogleSheetPicker from '../components/GoogleSheetPicker';
import { useApp } from '../context/AppContext';
import { uploadApi } from '../services/api';
import { UploadResponse } from '../types';

type Source = 'file' | 'gsheet';

const Upload: React.FC = () => {
  const navigate = useNavigate();
  const { uploadData, selectedSheets, setUploadData, setSelectedSheets, toggleSheetSelection } = useApp();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<Source>('file');

  // A Google-Sheet import lands here with the tabs already chosen, so it skips straight
  // to column confirmation rather than repeating the sheet picker.
  const handleImported = useCallback((res: UploadResponse) => {
    setUploadData(res);
    setSelectedSheets(res.sheets.map((s) => s.name));
    navigate('/column-preview');
  }, [navigate, setUploadData, setSelectedSheets]);

  const onDrop = useCallback(async (files: File[]) => {
    if (!files.length) return;
    setIsUploading(true);
    setError(null);
    try {
      const res = await uploadApi.uploadFile(files[0]);
      setUploadData(res);
      setSelectedSheets(res.sheets.map((s) => s.name));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  }, [setUploadData, setSelectedSheets]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
    },
    maxFiles: 1,
  });

  const handleNext = () => {
    if (!uploadData || selectedSheets.length === 0) return;
    navigate('/column-preview');
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <h2 style={{ color: 'var(--text-strong)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Upload Lead File</h2>

      {error && (
        <div style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)', padding: 12, borderRadius: 8, marginBottom: 16 }}>
          ❌ {error}
        </div>
      )}

      {!uploadData ? (
        <>
          {/* Source switch — same position and size in both states, no layout shift. */}
          <div style={{ display: 'inline-flex', gap: 0, marginBottom: 12, border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
            {(['file', 'gsheet'] as Source[]).map((opt) => (
              <button
                key={opt}
                onClick={() => { setSource(opt); setError(null); }}
                style={{
                  padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
                  background: source === opt ? 'var(--status-analysis-processing-bg)' : 'var(--surface)',
                  color: source === opt ? 'var(--text-strong)' : 'var(--text-soft)',
                }}
              >
                {opt === 'file' ? 'Upload file' : 'Google Sheet'}
              </button>
            ))}
          </div>

          {source === 'gsheet' ? (
            <GoogleSheetPicker onImported={handleImported} />
          ) : (
        <div
          {...getRootProps()}
          style={{
            border: `2px dashed ${isDragActive ? 'var(--amber)' : 'var(--border)'}`,
            borderRadius: 8, padding: '3rem', textAlign: 'center', cursor: 'pointer',
            background: isDragActive ? 'var(--status-analysis-processing-bg)' : 'var(--surface)',
          }}
        >
          <input {...getInputProps()} />
          {isUploading ? (
            <p>⏳ Uploading and parsing file…</p>
          ) : isDragActive ? (
            <p>📁 Drop the file here…</p>
          ) : (
            <>
              <p style={{ fontSize: 48, margin: 0 }}>📄</p>
              <p>Drag and drop a file here, or click to select</p>
              <p style={{ color: 'var(--text-faint)', fontSize: 13 }}>Supports .xlsx, .xls, and .csv files</p>
            </>
          )}
        </div>
          )}
        </>
      ) : (
        <div>
          {/* File info */}
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 16, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, background: 'var(--surface)' }}>
            <span style={{ fontSize: 32 }}>📊</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, color: 'var(--text-strong)' }}>{uploadData.filename}</div>
              <div style={{ color: 'var(--text-soft)', fontSize: 13 }}>{uploadData.total_rows} rows · {uploadData.sheets.length} sheet{uploadData.sheets.length !== 1 ? 's' : ''}</div>
            </div>
            <button
              onClick={() => { setUploadData(null); setSelectedSheets([]); }}
              style={{ background: 'var(--surface-subtle)', color: 'var(--text)', border: '1px solid var(--border)', padding: '8px 16px', borderRadius: 6, cursor: 'pointer' }}
            >
              Change File
            </button>
          </div>

          {/* Sheet selection */}
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 16, marginBottom: 20, background: 'var(--surface)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0, color: 'var(--text-strong)' }}>Select Sheets to Process</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setSelectedSheets(uploadData.sheets.map((s) => s.name))} style={{ fontSize: 12, padding: '4px 10px', cursor: 'pointer', border: '1px solid var(--border)', borderRadius: 4, background: 'var(--surface-subtle)', color: 'var(--text)' }}>All</button>
                <button onClick={() => setSelectedSheets([])} style={{ fontSize: 12, padding: '4px 10px', cursor: 'pointer', border: '1px solid var(--border)', borderRadius: 4, background: 'var(--surface-subtle)', color: 'var(--text)' }}>None</button>
              </div>
            </div>
            <p style={{ color: 'var(--text-soft)', fontSize: 13, margin: '0 0 12px' }}>{selectedSheets.length} of {uploadData.sheets.length} selected</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {uploadData.sheets.map((sheet) => (
                <div
                  key={sheet.name}
                  onClick={() => toggleSheetSelection(sheet.name)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                    border: `2px solid ${selectedSheets.includes(sheet.name) ? 'var(--amber)' : 'var(--border)'}`,
                    borderRadius: 8, cursor: 'pointer',
                    background: selectedSheets.includes(sheet.name) ? 'var(--status-analysis-processing-bg)' : 'var(--surface)',
                  }}
                >
                  <input type="checkbox" checked={selectedSheets.includes(sheet.name)} onChange={() => {}} />
                  <div>
                    <span style={{ fontWeight: 600 }}>{sheet.name}</span>
                    <span style={{ color: 'var(--text-soft)', fontSize: 13, marginLeft: 8 }}>
                      {sheet.row_count} rows
                      {sheet.phone_columns.length > 0 && ` · Phone: ${sheet.phone_columns.join(', ')}`}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ textAlign: 'center' }}>
            <button
              onClick={handleNext}
              disabled={selectedSheets.length === 0}
              style={{
                background: selectedSheets.length === 0 ? 'var(--status-neutral-bg)' : 'linear-gradient(135deg, #8a2000, #5c0000)',
                color: selectedSheets.length === 0 ? 'var(--text-faint)' : 'var(--text)',
                border: '2px solid var(--teal)', padding: '12px 32px', borderRadius: 8,
                fontSize: 15, fontWeight: 600, cursor: selectedSheets.length === 0 ? 'not-allowed' : 'pointer',
              }}
            >
              Next: Confirm Columns →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Upload;
